import { archiveStats } from './archive'

export type Affiliation =
  | 'alumni'
  | 'staff'
  | 'volunteer'
  | 'board'
  | 'supporter'
  | 'family'

export interface Member {
  name: string
  /** Their main span. Kept for display; `spans` is the source of truth. */
  years: string
  /**
   * Every stretch they were involved, since involvement is rarely one
   * continuous block — a program in the 90s, back as a volunteer in the 2010s.
   * Defaults to `[years]`.
   */
  spans?: string[]
  /** Set when their most recent span is open-ended. */
  stillInvolved?: boolean
  decade: string
  affiliation: Affiliation
  city?: string
  note?: string
  /** Prototype placeholder portrait. Real portraits need explicit consent. */
  photo?: string
  programs?: string[]
  /** Shown on their profile if they chose to write one. */
  about?: string
  /** 'public' members appear in People; 'private' ones are only known to staff. */
  visibility?: 'public' | 'private'
  joined?: string
  /**
   * Optional, member-supplied. Only visible to another member after both
   * people have waved — a mutual, revocable handshake rather than a directory.
   */
  links?: { label: string; url: string }[]
  /** True when this member has waved back at you, unlocking their links. */
  wavedBack?: boolean
  /** Set when a member has died. Their profile becomes a memorial. */
  memorial?: {
    died: string
    /** Written by the family or by staff with the family's agreement. */
    notice: string
    /** Who confirmed it, so the page never looks like a rumour. */
    confirmedBy: string
    givingNote?: string
  }
}

export interface Tribute {
  memberName: string
  from: string
  years: string
  body: string
  when: string
}

export interface ArchiveEntry {
  year: string
  decade: string
  title: string
  kind: 'photograph' | 'note' | 'memory' | 'record'
  by?: string
}

export interface DispatchItem {
  kind: 'arrivals' | 'archive' | 'acknowledgement' | 'invite' | 'reflection'
  headline: string
  body: string
  action?: string
}

export const CURRENT_MEMBER = {
  firstName: 'Ray',
  fullName: 'Ray Mendel',
  publicName: 'Ray M.',
  years: '1994–1999',
  decade: '1990s',
}

export const DECADES = ['1970s', '1980s', '1990s', '2000s', '2010s', '2020s']

export const AFFILIATION_LABELS: Record<Affiliation, string> = {
  alumni: 'In a program',
  staff: 'Staff',
  volunteer: 'Volunteer',
  board: 'Board',
  supporter: 'Supporter',
  family: 'Family or friend',
}

const PORTRAITS = {
  marion: 'https://cdn.magicpatterns.com/patterns/generated-images/95c2dcd6-21ba-4c3c-8177-7a49aef3885a.jpg',
  ken: 'https://cdn.magicpatterns.com/patterns/generated-images/f9ab3a9b-362d-4d48-804c-96697490ac83.jpg',
  theresa: 'https://cdn.magicpatterns.com/patterns/generated-images/da6e330d-f103-4f3b-9ff3-193361f4ce5d.jpg',
  marcus: 'https://cdn.magicpatterns.com/patterns/generated-images/62e6ff55-ee44-41f5-af14-cb01a350ed14.jpg',
  nadia: 'https://cdn.magicpatterns.com/patterns/generated-images/0b2d9207-e8f3-4571-929c-e87052ff8c1b.jpg',
}

export const MEMBERS: Member[] = [
  {
    name: 'Marion Alvarez',
    years: '1978–1981',
    spans: ['1978–1981', '2009–present'],
    stillInvolved: true,
    decade: '1970s',
    affiliation: 'alumni',
    city: 'Hillcrest',
    note: 'One of the first thirty',
    photo: PORTRAITS.marion,
  },
  {
    name: 'Ken Doyle',
    years: '1979–1984',
    spans: ['1979–1984', '1998–2004'],
    decade: '1970s',
    affiliation: 'volunteer',
    city: 'La Mesa',
    photo: PORTRAITS.ken,
  },
  { name: 'Ruth Okonjo', years: '1977–1980', decade: '1970s', affiliation: 'staff', city: 'Palm Springs' },
  { name: 'Bill Traeger', years: '1979–1982', decade: '1970s', affiliation: 'alumni' },
  { name: 'Dev Rao', years: '1984–1989', decade: '1980s', affiliation: 'volunteer', city: 'North Park', note: 'Recovery Games organizer' },
  { name: 'Paulette Simms', years: '1985–1987', decade: '1980s', affiliation: 'alumni', city: 'Chula Vista' },
  { name: 'Gene Halloran', years: '1982–1986', decade: '1980s', affiliation: 'staff' },
  { name: 'Ivy Nakamura', years: '1988–1991', decade: '1980s', affiliation: 'alumni', city: 'Oceanside' },
  { name: 'Carl Whitfield', years: '1983–1985', decade: '1980s', affiliation: 'board' },
  {
    name: 'Ray Mendel',
    years: '1994–1999',
    decade: '1990s',
    affiliation: 'alumni',
    city: 'North Park',
    note: 'You',
    programs: ['Residential', 'Sober living'],
    about:
      'Came in through residential on Central Avenue in 1994 and stayed longer than I meant to. Still in North Park, still at the Tuesday alumni group some weeks.',
    visibility: 'public',
    joined: 'August 2026',
    links: [
      { label: 'LinkedIn', url: 'linkedin.com/in/raymendel' },
      { label: 'Personal site', url: 'raymendel.example' },
    ],
  },
  {
    name: 'Theresa Molina',
    years: '1996–1998',
    decade: '1990s',
    affiliation: 'alumni',
    city: 'University Heights',
    note: 'Enya House',
    programs: ['Residential', 'Sober living'],
    about: 'Two years that I think about most weeks. Happy to hear from anyone who was there.',
    visibility: 'public',
    joined: 'Yesterday',
    links: [{ label: 'Instagram', url: 'instagram.com/theresamolina' }],
    wavedBack: true,
    photo: PORTRAITS.theresa,
  },
  { name: 'Lorna Bright', years: '1993–1996', decade: '1990s', affiliation: 'alumni' },
  {
    name: 'Sean Duffy',
    years: '1991–1995',
    decade: '1990s',
    affiliation: 'staff',
    city: 'Golden Hill',
    programs: ['Aftercare and alumni', 'Sober living'],
    about:
      'Ran the Tuesday group for four years and never once started it late. Known for standing in the doorway until everyone was inside.',
    visibility: 'public',
    joined: 'March 2026',
    memorial: {
      died: 'June 2026',
      notice: 'Sean died in June 2026.',
      confirmedBy: 'Shared with his family’s agreement',
      givingNote:
        'In lieu of flowers, Sean’s family is encouraging support for the alumni programs he ran.',
    },
  },
  {
    name: 'Anita Cruz',
    years: '1997–2001',
    decade: '1990s',
    affiliation: 'alumni',
    city: 'City Heights',
    links: [{ label: 'LinkedIn', url: 'linkedin.com/in/anitacruz' }],
  },
  {
    name: 'Howard Pike',
    years: '1995–present',
    spans: ['1995–present'],
    stillInvolved: true,
    decade: '1990s',
    affiliation: 'supporter',
    note: 'Alongside since 1995',
  },
  { name: 'Andre Price', years: '2004–2009', decade: '2000s', affiliation: 'volunteer', city: 'Hillcrest', note: 'Mentor since 2004' },
  { name: 'Danielle Ward', years: '2002–2005', decade: '2000s', affiliation: 'alumni', city: 'Escondido' },
  { name: 'Priya Nair', years: '2006–2008', decade: '2000s', affiliation: 'staff' },
  { name: 'Owen Tate', years: '2003–2007', decade: '2000s', affiliation: 'alumni' },
  { name: 'Grace Lindberg', years: '2008–2011', decade: '2000s', affiliation: 'board', city: 'Long Beach' },
  {
    name: 'Nadia Kaur',
    years: '2013–2016',
    spans: ['2013–2016', '2021–present'],
    stillInvolved: true,
    decade: '2010s',
    affiliation: 'alumni',
    city: 'Normal Heights',
    photo: PORTRAITS.nadia,
  },
  {
    name: 'Marcus Jean',
    years: '2011–2014',
    decade: '2010s',
    affiliation: 'alumni',
    photo: PORTRAITS.marcus,
  },
  { name: 'Elena Vasquez', years: '2015–2019', decade: '2010s', affiliation: 'staff', city: 'South Park' },
  { name: 'Toby Fenn', years: '2017–2019', decade: '2010s', affiliation: 'volunteer' },
  { name: 'Simone Adjei', years: '2012–2015', decade: '2010s', affiliation: 'alumni', city: 'Hillcrest' },
  { name: 'Sam Okafor', years: '2021–present', decade: '2020s', affiliation: 'volunteer', city: 'North Park', note: 'Pride crew' },
  { name: 'Iris Lund', years: '2020–2023', decade: '2020s', affiliation: 'alumni' },
  { name: 'Jonah Reyes', years: '2022–present', decade: '2020s', affiliation: 'alumni', city: 'Chula Vista' },
]

export const RECENT_JOINS: { name: string; decade: string; when: string }[] = [
  { name: 'Theresa M.', decade: '1990s', when: 'Yesterday' },
  { name: 'Lorna B.', decade: '1990s', when: '2 days ago' },
  { name: 'Sam O.', decade: '2020s', when: '3 days ago' },
  { name: 'Ken D.', decade: '1970s', when: 'Last week' },
  { name: 'Danielle W.', decade: '2000s', when: 'Last week' },
]

export const ARCHIVE_ENTRIES: ArchiveEntry[] = [
  { year: '1976', decade: '1970s', title: 'The first meeting, at MCC on Fern Street', kind: 'record' },
  { year: '1989', decade: '1980s', title: '“The back house on Central saved my life twice.”', kind: 'memory', by: 'Dev R.' },
  { year: '1994', decade: '1990s', title: 'The Central Avenue houses become ours', kind: 'record' },
  { year: '1996', decade: '1990s', title: 'Friday night, outside after the meeting', kind: 'photograph' },
  { year: '1997', decade: '1990s', title: '“I learned to ask for help in that building.”', kind: 'memory', by: 'Theresa M.' },
  { year: '2000', decade: '2000s', title: 'The Stone, the summer it opened', kind: 'photograph' },
  { year: '2004', decade: '2000s', title: 'Living Out Loud on the USS Midway', kind: 'record' },
  { year: '2007', decade: '2000s', title: 'Alumni camping trip, up in the mountains', kind: 'photograph' },
  { year: '2010', decade: '2010s', title: 'Nonprofit of the Year', kind: 'record' },
  { year: '2022', decade: '2020s', title: '“Everyone Belongs” night on the front steps', kind: 'photograph' },
]

export const DISPATCH_ITEMS: DispatchItem[] = [
  {
    kind: 'arrivals',
    headline: 'Two people from your years arrived',
    body: 'Theresa M. and Lorna B. both joined this week.',
    action: 'See who else is from 1994–1999',
  },
]

export const SPARSE_MEMBER_LIMIT = 12

export function scaleMembers(sparse: boolean): Member[] {
  if (!sparse) return MEMBERS
  const perDecade: Record<string, number> = {}
  return MEMBERS.filter((member) => {
    const seen = perDecade[member.decade] ?? 0
    if (seen >= 2 && member.name !== CURRENT_MEMBER.fullName) return false
    perDecade[member.decade] = seen + 1
    return true
  })
}

export function membersByDecade(members: Member[], decade: string): Member[] {
  return members.filter((member) => member.decade === decade)
}

export function decadeCounts(sparse: boolean): Record<string, number> {
  const members = scaleMembers(sparse)
  return DECADES.reduce<Record<string, number>>((counts, decade) => {
    counts[decade] = membersByDecade(members, decade).length
    return counts
  }, {})
}

export function communityTotals(sparse: boolean) {
  const members = scaleMembers(sparse)
  const stats = archiveStats()
  return {
    members: members.length,
    decades: DECADES.filter((decade) => membersByDecade(members, decade).length > 0).length,
    collectiveYears: sparse ? '164' : '1,850',
    memoriesShared: sparse ? 4 : 46,
    /** Same source as the landing page, so both always agree. */
    archiveItems: sparse ? 31 : stats.total,
    /** Of the fifty years, how many have at least one archive entry. */
    yearsCovered: sparse ? 19 : stats.yearsCovered,
    yearsTotal: 50,
  }
}

/** Who joined in the last seven days, org-wide, with the decade breakdown. */
export function arrivalsThisWeek(sparse: boolean) {
  const people = RECENT_JOINS.slice(0, sparse ? 2 : 5)
  const byDecade = people.reduce<Record<string, number>>((counts, person) => {
    counts[person.decade] = (counts[person.decade] ?? 0) + 1
    return counts
  }, {})
  const top = Object.entries(byDecade).sort((a, b) => b[1] - a[1])[0]
  return {
    people,
    count: sparse ? 2 : 9,
    topDecade: top ? { decade: top[0], count: top[1] } : null,
  }
}

/** First name + last initial. The only public name shape. */
export function publicName(name: string): string {
  const [first, last] = name.split(' ')
  return last ? `${first} ${last.charAt(0)}.` : first
}

export const TIMELINE_START = 1976
export const TIMELINE_END = 2026
export const TIMELINE_SPAN = TIMELINE_END - TIMELINE_START

/** "1994–1999" / "2021–present" → numeric span. */
export function parseYears(years: string): { start: number; end: number } {
  const [rawStart, rawEnd] = years.split('–')
  const start = Number(rawStart)
  const end = rawEnd === 'present' ? TIMELINE_END : Number(rawEnd || rawStart)
  return { start, end }
}

/** Every stretch a member was involved, as numeric spans. */
export function memberSpans(member: Member): { start: number; end: number; open: boolean }[] {
  const raw = member.spans?.length ? member.spans : [member.years]
  return raw.map((span) => ({ ...parseYears(span), open: span.endsWith('present') }))
}

/** The years a member names as their span, read as a sentence. */
export function spanLabel(member: Member): string {
  const raw = member.spans?.length ? member.spans : [member.years]
  return raw.join(', ')
}

export const TRIBUTES: Tribute[] = [
  {
    memberName: 'Sean Duffy',
    from: 'Theresa M.',
    years: '1996–1998',
    body: 'He is the reason I came back the second week. He didn’t say anything about the first week at all.',
    when: 'June 2026',
  },
  {
    memberName: 'Sean Duffy',
    from: 'Anita C.',
    years: '1997–2001',
    body: 'Sean drove me to a job interview in 1998 in his own car and waited in the parking lot for an hour and a half.',
    when: 'June 2026',
  },
  {
    memberName: 'Sean Duffy',
    from: 'A member who chose not to be named',
    years: '1990s',
    body: 'I never told him what those Tuesdays did for me. I assumed there would be time.',
    when: 'July 2026',
  },
]

export function tributesFor(name: string): Tribute[] {
  return TRIBUTES.filter((tribute) => tribute.memberName === name)
}

export function memberByName(name: string): Member | undefined {
  return MEMBERS.find((member) => member.name === name)
}

/**
 * Years shared with the signed-in member, across every stretch either of them
 * names. Never framed as more or less.
 */
export function overlapWith(member: Member, other: Member | typeof CURRENT_MEMBER) {
  const mine = 'spans' in other && other.spans?.length ? other.spans : [other.years]
  const theirs = member.spans?.length ? member.spans : [member.years]

  for (const theirSpan of theirs) {
    const a = parseYears(theirSpan)
    for (const mySpan of mine) {
      const b = parseYears(mySpan)
      const start = Math.max(a.start, b.start)
      const end = Math.min(a.end, b.end)
      if (end >= start) return { start, end, years: Math.max(end - start, 1) }
    }
  }
  return null
}
